import { T, tt } from '@agos/shared'
import { Trash2, X } from 'lucide-react'
import { useState, type ReactNode } from 'react'

import type { RestaurantTable, TableWriteFields } from './types'
import { type ZoneOption } from './zones'
import {
  BUTTON_GHOST,
  BUTTON_PRIMARY,
  cn,
} from '@agos/ui'

/**
 * The edit-mode panel for one table.
 *
 * Its fields are the ones a restaurant actually changes when it moves
 * furniture: the label on the tile, how many people fit, the shape, the size,
 * the zone and a colour. Position is not here — that is the drag.
 *
 * **The zone is a picker since §9.337**, not a text box. A register hangs off a zone
 * (`BACKEND.md` §10A.125) and nothing can hang off a string somebody types twice - which is what
 * `restaurant_tables.zone` was, and NULL on all 11 of the owner's rows.
 */

const FIELD_CLASSES =
  'w-full px-2 py-1.5 rounded-lg border border-border bg-background text-sm focus:outline-none focus:ring-2 focus:ring-primary/30'

export interface TableInspectorProps {
  table: RestaurantTable
  /** The real zones plus any free text the tables still carry - §9.337. */
  zones: ZoneOption[]
  onSave: (changes: TableWriteFields) => void
  onDelete: () => void
  onClose: () => void
}

export function TableInspector({ table, zones, onSave, onDelete, onClose }: TableInspectorProps) {
  const [name, setName] = useState(table.name)
  const [capacity, setCapacity] = useState(String(table.capacity))
  const [shape, setShape] = useState<RestaurantTable['shape']>(table.shape)
  const [width, setWidth] = useState(String(table.width))
  const [height, setHeight] = useState(String(table.height))
  /**
   * The zone as a key, so a real zone and a leftover string are both selectable.
   *
   * Real zone first, matching `tableIsIn`: a table put in a zone may still carry the old text -
   * §10A.117 left it there on purpose - and in that case the real zone is what it is in.
   */
  const [zoneChoice, setZoneChoice] = useState(() => {
    if (table.zoneId != null) return `z:${table.zoneId}`
    const text = (table.zone ?? '').trim()
    return text === '' ? '' : `t:${text}`
  })
  const [color, setColor] = useState(table.color ?? '')

  /**
   * **Both zone fields are sent, and which one carries the value is the rule.**
   *
   * Choosing a real zone sets `zoneId` and **clears the text**, because leaving a stale string
   * behind is how a table ends up filed under a name it no longer belongs to. Choosing a text-only
   * zone sets the text and clears `zoneId`. *Bez zóny* clears both. `null` clears on this endpoint
   * and `undefined` would leave the stored value alone, which is the distinction `TableWriteFields`
   * exists to express.
   */
  const save = () => {
    const realZone = zoneChoice.startsWith('z:') ? Number(zoneChoice.slice(2)) : null
    const textZone = zoneChoice.startsWith('t:') ? zoneChoice.slice(2) : null
    onSave({
      name: name.trim() || table.name,
      capacity: Number(capacity) || table.capacity,
      shape,
      width: Number(width) || table.width,
      height: Number(height) || table.height,
      zone: textZone,
      zoneId: realZone,
      color: color.trim() || null,
    })
  }

  return (
    <div className="bg-card border border-border rounded-2xl p-4 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold">
          <T>Stôl</T> {table.name}
        </h3>
        <button
          type="button"
          aria-label={tt('Zatvoriť')}
          onClick={onClose}
          className={BUTTON_GHOST}
        >
          <X className="w-4 h-4" />
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Field label={tt('Názov')}>
          <input value={name} onChange={(event) => setName(event.target.value)} className={FIELD_CLASSES} />
        </Field>
        <Field label={tt('Kapacita')}>
          <input
            type="number"
            min="1"
            value={capacity}
            onChange={(event) => setCapacity(event.target.value)}
            className={FIELD_CLASSES}
          />
        </Field>
        <Field label={tt('Tvar')}>
          <select
            value={shape}
            onChange={(event) => setShape(event.target.value as RestaurantTable['shape'])}
            className={FIELD_CLASSES}
          >
            <option value="rect">{tt('Obdĺžnik')}</option>
            <option value="circle">{tt('Kruh')}</option>
          </select>
        </Field>
        <Field label={tt('Zóna')}>
          <select
            value={zoneChoice}
            onChange={(event) => setZoneChoice(event.target.value)}
            data-table-zone
            className={FIELD_CLASSES}
          >
            <option value="">{tt('Bez zóny')}</option>
            {zones.map((option) => (
              <option
                key={option.id === null ? `t:${option.name}` : `z:${option.id}`}
                value={option.id === null ? `t:${option.name}` : `z:${option.id}`}
              >
                {/* A text-only zone is marked, because it is the one that cannot carry a register
                  * and the owner's whole request is about registers. */}
                {option.id === null ? `${option.name} (${tt('len text')})` : option.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label={tt('Šírka')}>
          <input
            type="number"
            min="20"
            value={width}
            onChange={(event) => setWidth(event.target.value)}
            className={FIELD_CLASSES}
          />
        </Field>
        <Field label={tt('Výška')}>
          <input
            type="number"
            min="20"
            value={height}
            onChange={(event) => setHeight(event.target.value)}
            className={FIELD_CLASSES}
          />
        </Field>
        <Field label={tt('Farba')}>
          <div className="flex gap-1.5">
            <input
              type="color"
              value={color || '#888888'}
              onChange={(event) => setColor(event.target.value)}
              aria-label={tt('Farba')}
              className="h-9 w-10 rounded-lg border border-border bg-background"
            />
            <button
              type="button"
              onClick={() => setColor('')}
              className="flex-1 px-2 rounded-lg border border-border text-xs font-semibold hover:bg-muted transition-colors"
            >
              <T>Zrušiť</T>
            </button>
          </div>
        </Field>
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={save}
          className={cn('px-4 py-2 rounded-lg', BUTTON_PRIMARY, 'text-xs')}
        >
          <T>Uložiť</T>
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-red-500/10 text-red-500 border border-red-500/20 text-xs font-bold hover:bg-red-500/20 transition-colors"
        >
          <Trash2 className="w-3.5 h-3.5" /> <T>Zmazať</T>
        </button>
      </div>
    </div>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wider">
        {label}
      </span>
      {children}
    </label>
  )
}
